import { useRef } from "react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import useReveal from "@/hooks/useReveal";

export default function CoreValues() {
  const ref = useRef(null);
  const { t, lang } = useT();

  const pillars = [
    { tag: "01", title: t("about.p1.title"), desc: t("about.p1.desc") },
    { tag: "02", title: t("about.p2.title"), desc: t("about.p2.desc") },
    { tag: "03", title: t("about.p3.title"), desc: t("about.p3.desc") },
  ];

  const headline = `${t("about.titleA")} ${t("about.titleHL")} ${t("about.titleB")}`;

  useReveal(ref, () => {
    const ctx = gsap.context(() => {
      // Dramatic fade-in with stagger
      gsap.from("[data-anim='cv-headline']", {
        opacity: 0,
        y: 50,
        duration: 1.5,
        ease: "power3.out",
      });

      gsap.from("[data-anim='cv-pillars']", {
        opacity: 0,
        y: 40,
        duration: 1.2,
        delay: 0.4,
        ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  });

  return (
    <div
      ref={ref}
      data-testid="core-values-root"
      className="relative w-full h-screen overflow-hidden"
    >
      {/* Full Bleed Background Image - Subtle Backdrop */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')",
          filter: "brightness(0.2) saturate(0.7) blur(8px)",
        }}
      />

      {/* Dark Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: "rgba(12, 14, 20, 0.65)",
        }}
      />

      {/* Vignette Fade - All Sides */}
      <div 
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 0%, rgba(12, 14, 20, 0.75) 65%, rgba(12, 14, 20, 0.95) 100%)",
        }}
      />

      {/* Subtle Film Grain */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col justify-end px-5 sm:px-8 md:px-16 lg:px-24 pb-16 sm:pb-20 pt-24">
        {/* Main Headline - Large & Impactful */}
        <div data-anim="cv-headline" className="mb-8 sm:mb-12">
          <h2
            className="font-display text-white mb-6 sm:mb-8"
            style={{
              fontSize: "clamp(2rem, 7vw, 7.5rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.04em",
              fontWeight: 700,
              maxWidth: "100%",
            }}
          >
            {headline.split(" ").map((w, i) => {
              const isHL = w.toLowerCase() === t("about.titleHL").toLowerCase();
              return (
                <span
                  key={i}
                  className={isHL ? "italic" : ""}
                  style={
                    isHL
                      ? {
                          fontFamily: "Cormorant Garamond, serif",
                          fontWeight: 500,
                          color: "transparent",
                          WebkitTextStroke: "1.5px #b8e637",
                          textStroke: "1.5px #b8e637",
                        }
                      : {}
                  }
                >
                  {w}{" "}
                </span>
              );
            })}
          </h2>

          <p 
            className="text-white/70 leading-relaxed max-w-2xl"
            style={{
              fontSize: "clamp(0.9375rem, 1.5vw, 1.25rem)",
              lineHeight: 1.65,
            }}
          >
            {t("about.body")}
          </p>
        </div>

        {/* Horizontal Scrollable Pillars */}
        <div 
          data-anim="cv-pillars"
          className="relative"
        >
          <div 
            className="flex gap-8 sm:gap-10 md:gap-12 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <style>{`
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            
            {pillars.map((p, index) => (
              <div
                key={p.tag}
                className="flex-none w-[85vw] sm:w-[70vw] md:w-[45vw] lg:w-[30vw] snap-start"
              >
                {/* Number */}
                <div 
                  className="font-display mb-3 sm:mb-4 text-[#b8e637]"
                  style={{
                    fontSize: "clamp(0.95rem, 1.2vw, 1.2rem)",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                  }}
                >
                  {p.tag}
                </div>

                {/* Title */}
                <h3
                  className="font-display text-white mb-3 sm:mb-4"
                  style={{
                    fontSize: "clamp(1.375rem, 2.5vw, 2.25rem)",
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                  }}
                >
                  {p.title}
                </h3>

                {/* Description */}
                <p 
                  className="text-white/60 leading-relaxed"
                  style={{
                    fontSize: "clamp(0.875rem, 1vw, 1rem)",
                    lineHeight: 1.65,
                  }}
                >
                  {p.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Navigation Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {pillars.map((p, index) => (
              <button
                key={p.tag}
                onClick={() => {
                  const container = document.querySelector('.overflow-x-auto');
                  const item = container?.children[index];
                  item?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                }}
                className="w-2 h-2 rounded-full bg-white/30 hover:bg-white/60 transition-colors"
                aria-label={`Go to ${p.title}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
